
import React from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from "@/hooks/use-toast";
import { Skeleton } from "@/components/ui/skeleton";
import { useJournalistProfile } from '@/hooks/journalist';

import ProfileHeader from '@/components/journalist-profile/ProfileHeader';
import ProfileMetrics from '@/components/journalist-profile/ProfileMetrics';
import SubscriptionCard from '@/components/journalist-profile/SubscriptionCard';
import ArticleTab from '@/components/journalist-profile/ArticleTab';
import AllArticlesTab from '@/components/journalist-profile/AllArticlesTab';
import PremiumContentTab from '@/components/journalist-profile/PremiumContentTab';

const JournalistProfile = () => {
  const { toast } = useToast();
  const { userProfile } = useAuth();
  
  const {
    journalist,
    articles,
    isLoading,
    error,
    isFollowing,
    isOwnProfile,
    toggleFollow,
    updateProfile
  } = useJournalistProfile();
  
  const handleAvatarUpdate = async (url: string) => {
    if (!isOwnProfile) return;
    
    try {
      const { error } = await supabase
        .from('profiles')
        .update({ avatar_url: url })
        .eq('id', journalist?.id);
      
      if (error) throw error;
      
      updateProfile({ avatar: url });
      
      toast({
        title: 'Avatar updated',
        description: 'Your profile picture has been updated successfully.'
      });
    } catch (error) {
      console.error('Error updating profile picture:', error);
      toast({
        variant: 'destructive',
        title: 'Update failed',
        description: 'There was a problem updating your profile picture.',
      });
    }
  };
  
  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <div className="h-64 w-full bg-gray-200" />
        <div className="container mx-auto px-4 relative -mt-24">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 mb-8">
            <div className="flex flex-col md:flex-row gap-6">
              <Skeleton className="h-32 w-32 rounded-full" />
              <div className="flex-grow space-y-4">
                <Skeleton className="h-8 w-1/3" />
                <Skeleton className="h-4 w-1/2" />
                <Skeleton className="h-24 w-full" />
                <div className="flex flex-wrap gap-2 mb-6">
                  <Skeleton className="h-6 w-20" />
                  <Skeleton className="h-6 w-24" />
                  <Skeleton className="h-6 w-16" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
  
  if (error || !journalist) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center p-8">
          <h1 className="text-2xl font-bold mb-4">Journalist Not Found</h1>
          <p className="text-muted-foreground mb-6">
            {error?.message || "We couldn't find the journalist you're looking for."}
          </p>
          <a href="/" className="text-primary hover:underline">
            Return to home page
          </a>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 relative -mt-24">
        <ProfileHeader 
          journalist={journalist}
          isOwnProfile={isOwnProfile}
          userProfile={userProfile}
          onAvatarUpdate={handleAvatarUpdate}
          isFollowing={isFollowing}
          toggleFollow={toggleFollow}
          updateProfile={updateProfile}
        />
        
        <ProfileMetrics 
          metrics={journalist.metrics}
          credentials={journalist.credentials}
          isOwnProfile={isOwnProfile}
          updateProfile={updateProfile}
        />
        
        <SubscriptionCard 
          name={journalist.name}
          subscription={journalist.subscription}
          isOwnProfile={isOwnProfile}
          updateProfile={updateProfile}
        />
        
        <Tabs defaultValue="featured" className="mb-16">
          <TabsList className="w-full md:w-auto">
            <TabsTrigger value="featured">Featured</TabsTrigger>
            <TabsTrigger value="all-articles">All Articles</TabsTrigger>
            <TabsTrigger value="premium">Premium Content</TabsTrigger>
          </TabsList>
          
          <TabsContent value="featured">
            <ArticleTab articles={articles} />
          </TabsContent>
          
          <TabsContent value="all-articles">
            <AllArticlesTab articles={articles} />
          </TabsContent>
          
          <TabsContent value="premium">
            <PremiumContentTab articles={articles.filter(article => article.premium)} />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default JournalistProfile;
